import { ClearRounded, DoNotDisturbOnOutlined, ExpandMoreRounded } from "@mui/icons-material";
import { alpha, Autocomplete as MuiAutocomplete, AutocompleteProps, CircularProgress, styled } from "@mui/material";
import React from "react";

const StyledMuiAutocomplete = styled(MuiAutocomplete)`
  .MuiInputLabel-root {
    color: ${({ theme }) => theme.palette.primary.main};

    &.Mui-disabled {
      color: ${({ theme }) => theme.palette.text.disabled};
    }
  }

  .MuiAutocomplete-inputRoot {
    min-height: 48px;
    padding: 6px 12px;
  }

  .MuiAutocomplete-endAdornment .MuiSvgIcon-root {
    color: ${({ theme }) => theme.palette.text.primary};
  }

  .MuiChip-root {
    height: 28px;
    background-color: ${({ theme }) => theme.palette.primary.opacity10};
    color: ${({ theme }) => theme.palette.primary.main};

    .MuiChip-label {
      font-size: ${({ theme }) => theme.typography.body1.fontSize};
      text-transform: none;
    }
  }

  .MuiChip-deleteIcon {
    color: ${({ theme }) => alpha(theme.palette.primary.main, 0.6)};
    font-size: 16px;
    &:hover {
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }
` as typeof MuiAutocomplete;

interface IProps<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  isLoading: boolean;
}

const Autocomplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: IProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: React.Ref<unknown>,
) => {
  const { isLoading, ...other } = props;

  return (
    <StyledMuiAutocomplete
      ref={ref}
      blurOnSelect
      handleHomeEndKeys
      clearIcon={<ClearRounded sx={{ fontSize: "20px" }} />}
      popupIcon={
        isLoading ? (
          <CircularProgress size={20} sx={{ color: "text.disabled" }} />
        ) : props.disabled ? null : (
          <ExpandMoreRounded sx={{ color: "primary.main", fontSize: "24px" }} />
        )
      }
      slotProps={{ chip: { deleteIcon: <DoNotDisturbOnOutlined sx={{ fontSize: "16px" }} /> } }}
      {...other}
    />
  );
};

const RefForwardedAutocomplete = React.forwardRef(Autocomplete) as <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: IProps<T, Multiple, DisableClearable, FreeSolo> & { ref?: React.Ref<unknown> },
) => React.ReactElement;

export { RefForwardedAutocomplete as Autocomplete };
